import { Section, Sub, Card, Note, Warn, Fields, Code, Shot } from '../../../components/docs/Blocks';

export default function LevelEditor() {
  return (
    <Section
      id="level-editor"
      title="Level Editor"
      lead="Everything Minecraft stores in level.dat — the file that holds a world's settings rather than its terrain. This is where a world is renamed, its difficulty changed, its game rules toggled and its experiments turned on or off."
    >
      <Sub id="world-info" title="World Info">
        <Shot
          name="level-level.info"
          alt="The World Info screen showing world name, seed, type, game mode and difficulty"
        />
        <Card>
          <Fields
            items={[
              ['World Name', 'The name shown in the Minecraft world list. Renaming here writes both level.dat and levelname.txt, which is what the launcher reads — if the two have drifted apart, Toolchest says so and offers to match them.'],
              ['World Seed', 'The number the terrain was generated from. Whole numbers only. Changing it affects chunks generated from now on, so existing terrain will not match the new seed.'],
              ['World Type', 'Infinite, Flat or Legacy. Changing it also only affects chunks generated from now on.'],
              ['Game Mode', 'The default mode for players joining the world: Survival, Creative, Adventure or Spectator.'],
              ['Difficulty', 'Peaceful, Easy, Normal or Hard.'],
              ['World Path', 'Where the world lives, with a button to open the folder.'],
              ['Database Size', 'How much disk the world database is using. Updates after pruning or a dimension reset.'],
              ['Game Version', 'The Minecraft version the world was last opened with. Information only.'],
              ['World Icon', 'The thumbnail Minecraft shows. Change it to any JPEG — Minecraft reads world_icon.jpeg, so PNGs are refused rather than silently ignored.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="gameplay" title="Gameplay">
        <Card>
          <p>Where players spawn, and the world-level switches that are not game rules.</p>
          <Fields
            items={[
              ['Spawn X / Y / Z', 'Where new players appear and where players respawn without a bed. Spawn Y can be set to Auto-detect, which drops the player on the first safe block instead of a fixed height — step past 320 with the arrows to reach it.'],
              ['Spawn Radius', 'How far from the spawn point players can appear. Zero for an exact spawn.'],
              ['Hardcore Mode', 'Players cannot respawn after death.'],
              ['Force Game Mode', 'Everyone is put into the world default when they join.'],
              ['Bonus Chest', 'A starter chest spawns near world spawn.'],
              ['Start with Map', 'New players spawn holding a map.'],
              ['Show Coordinates / Show Days Played', 'The on-screen readouts.'],
              ['Center Maps to Origin', 'Maps align to the world grid instead of centring where they were made.'],
              ['LAN Broadcast / Multiplayer Game', 'Whether the world is offered on the local network and to online players.'],
              ['Has Been Loaded in Creative', 'The flag that disables achievements. Clearing it — with cheats off and experiments off — is what restores achievements to a world that lost them.'],
              ['Locked Behaviour / Resource Pack, Texture Packs Required', 'Pack requirements for joining players.'],
              ['Chunk Tick Range', 'Simulation distance in chunks. Higher means a larger active area and more work for the machine running the world.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="gamerules" title="Gamerules">
        <Shot
          name="level-level.gamerules"
          alt="The Gamerules screen with rules grouped into categories"
        />
        <Card>
          <p>
            The same rules the <Code>/gamerule</Code> command sets, as switches
            grouped by what they affect. There is a filter box at the top, and the
            summary line counts how many rules the world actually carries — a rule
            Minecraft has never written is shown as <em>not set</em> rather than
            guessed at.
          </p>
          <Fields
            items={[
              ['Commands and Cheats', 'Cheats enabled, command blocks enabled, command block output, send command feedback.'],
              ['World Mechanics', 'Daylight cycle, weather cycle, fire tick, mob spawning, mob griefing, natural regeneration.'],
              ['Drops and Damage', 'Entity drops, mob loot, block drops, and the drowning, fall, fire and freeze damage switches.'],
              ['Player Settings', 'Keep inventory, immediate respawn, insomnia (phantoms), death messages, recipe messages, show tags, and player versus player.'],
              ['Advanced Settings', 'The numeric rules: random tick speed (crop growth and leaf decay, default 1), sleep percentage, function command limit and max command chain length.'],
            ]}
          />
          <p>
            Every rule carries a tooltip explaining what it does — including the ones
            whose names give nothing away, such as TNT drop decay and respawn blocks
            explode.
          </p>
        </Card>
      </Sub>

      <Sub id="experiments" title="Experiments">
        <Card>
          <p>
            The experimental feature toggles Minecraft offers when a world is created.
            Toolchest groups them by whether they are still available in the current
            game, still available in Preview, or retired — a world can carry a flag for
            an experiment that no longer exists, and hiding those would leave you
            unable to clear them.
          </p>
          <p>
            Since 3.1 the names and descriptions come from Minecraft&rsquo;s own
            files, in your language where the game has it, up to date with 1.26.52
            and Preview 1.26.70. Older experiments a world may still carry, such as
            Bundles, Locator Bar and the 2025 drops, show by name.
          </p>
          <Fields
            items={[
              ['Available in Minecraft', 'Experiments the installed version still offers.'],
              ['Also in this world', 'Flags the world carries that the current version has retired, such as Holiday Creator Features or Data-Driven Biomes.'],
              ["Minecraft's own flags", "Bookkeeping Minecraft maintains for itself. Toolchest keeps these in step with the toggles — turning every experiment off clears them, which is what restores achievements to a world that lost them to experiments."],
            ]}
          />
          <Warn>
            Minecraft will not let you turn an experiment off once a world has used
            it. Toolchest will. If your world contains blocks, items or behaviour that
            only exist because of an experiment, turning it off can remove them — take
            a backup first, which is what the default settings already do for you.
          </Warn>
        </Card>
      </Sub>

      <Sub id="abilities" title="Abilities">
        <Card>
          <p>
            The default abilities every player gets on joining, unless they have been
            overridden for that player. Split into permissions and movement speeds.
          </p>
          <Fields
            items={[
              ['Permissions', 'Attack mobs, attack players, build, mine, use redstone, open containers, instant build, invulnerable, may fly, teleport and operator.'],
              ['Movement Speeds', 'Walk speed (default 0.1), fly speed (default 0.05) and vertical fly speed (default 1).'],
            ]}
          />
          <Note>
            These are the world’s defaults. A single player’s abilities live on their
            own record and are edited from the Player Editor’s Advanced tab.
          </Note>
        </Card>
      </Sub>

      <Sub id="flat-world" title="Flat World">
        <Shot
          name="level-level.flat"
          alt="The Flat World layer editor showing block layers, depths and heights"
        />
        <Card>
          <p>
            The layer list a superflat world generates from. Only usable on a world
            whose type is Flat — on any other world the screen says so and points you
            at World Info to change the type.
          </p>
          <Fields
            items={[
              ['Layers', 'Listed top first, so the last row is the lowest block — usually bedrock. Each row is a block and a depth, and the height column shows where that layer ends up counting from the bottom of the world.'],
              ['Block', 'Click a block box and type to search — "grass", "stone", or a full id such as minecraft:podzol. You can also type an id Toolchest does not know and it will be kept as written.'],
              ['Depth', 'How many blocks thick that layer is. Three dirt means three blocks of dirt stacked.'],
              ['Reordering', 'The arrows move a layer up or down; new layers are added on top.'],
              ['Biome', 'Sets the flat world biome, which drives grass colour, sky tint, weather and which mobs spawn.'],
              ['Preset', 'Shown for reference. It switches to a custom preset automatically once the layers differ from a stock one, because Minecraft ignores edited layers otherwise.'],
            ]}
          />
          <Note>
            Layer changes affect newly generated chunks only. Anywhere you have
            already been keeps the terrain it already has. Toolchest also warns if the
            total height exceeds the 384 blocks a Bedrock world allows.
          </Note>
        </Card>
      </Sub>

      <Sub id="level-advanced" title="Advanced">
        <Shot
          name="level-level.advanced"
          alt="The Advanced screen showing the raw level.dat NBT tree"
        />

        <Card>
          <p>
            The raw level.dat NBT as a tree: every tag the world carries, including
            everything without a dedicated editor elsewhere. Tags are typed, and each
            is marked with its type so a byte can be told from a short at a glance.
          </p>
          <Fields
            items={[
              ['Filter', 'Narrows the tree to matching tag names.'],
              ['Expand all / Collapse all', 'For working through a large tree.'],
              ['Editing', 'Click a value to edit it. Values are validated against the tag\'s own type and nothing else — a string that will not parse as the number it needs to be is refused.'],
              ['Changed tags', 'The summary line counts how many tags you have altered this session.'],
            ]}
          />
          <p>
            Some values are shown but not editable: entries inside a list, which have
            no stable address, and raw byte blobs, which are not text.
          </p>
          <Warn>
            This edits level.dat directly with no checks beyond the tag’s type, and it
            overrides the other tabs — set cheatsEnabled to 1 here and the Gameplay tab
            will show it on. Incorrect changes can break a world. As everywhere else,
            nothing is written until you save and a backup is taken first.
          </Warn>
        </Card>
      </Sub>
    </Section>
  );
}
